import classNames from 'classnames';
import { Checkbox } from '../Checkbox/Checkbox';
import { DropdownButton } from '../DropdownButton/DropdownButton';
import { ArrowDownIcon, SearchIcon, SearchIconBel } from '../icons';
import styles from './CheckboxSelect.module.scss';
import { ChangeEvent, useState } from 'react';
import _ from 'lodash';
import { themeValue } from "@kursk/themes";
import { Input } from "@kursk/components/ui/Input/Input";
import { useDeepEffect } from "@common/hooks/useDeepEffect";

export interface ICheckboxSelectOption {
  _id: number | string;
  name: string;
  sysName?: string;
}

interface IProps {
  className?: string;
  title: string;
  items: ICheckboxSelectOption[];
  selectedItems?: (number | string)[];
  disabledArray: { _id: number, name: string }[];
  onChange: (value: any) => Promise<void>;
  placeholder: string;
  withSearch?: boolean;
}

export function CheckboxSelect({
  className,
  title,
  items,
  selectedItems = [],
  onChange,
  placeholder,
  disabledArray,
  withSearch = true
}: IProps) {
  const [defaultSelected, defaultOptions] = selectedItems.length ?
    _.partition(items, ({ _id }) => _.includes(selectedItems, _id)) :
    [[], items];

  useDeepEffect(() => {
    if (defaultOptions.length !== options.length) {
      setOptions(defaultOptions);
    }

    if (defaultSelected.length !== selected.length) {
      setSelected(defaultSelected);
    }
  }, [items, selectedItems]);

  const [selected, setSelected] = useState(defaultSelected);
  const [options, setOptions] = useState(defaultOptions);

  const totalOptions = [...options.filter((opt) => !disabledArray.find((i) => i.name === opt.name)),
    ...disabledArray].filter((el) => selected.find((item) => item._id === el._id)?._id !== el._id);

  const onSubmit = async () => {
    const values: (number | string)[] = _.map(selected, '_id');
    await onChange(values);
  }

  const onReset = async () => {
    setOptions(_.sortBy([...options, ...selected], 'name'));
    setSelected([]);
    await onChange([]);
  }

  const onSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event?.currentTarget;
    const regexp = new RegExp(value.toLowerCase());
    const excludeIds = _.map(selected, '_id');

    const result = _(items)
      .filter(({ _id }) => !excludeIds.includes(_id))
      .filter(({ name }) => regexp.test(name.toLowerCase()))
      .value();

    setOptions(result);
  }

  const onSelect = (item: any, event: ChangeEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const newSelected = _.sortBy([...selected, item], 'name');
    const newOptions = _.sortBy(_.without(options, item), 'name');

    setSelected(newSelected);
    setOptions(newOptions);
  }

  const onUnselect = (item: any, event: ChangeEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const newSelected = _.sortBy(_.without(selected, item), 'name');
    const newOptions = _.sortBy([...options, item], 'name');

    setSelected(newSelected);
    setOptions(newOptions);
  }

  return (
    <DropdownButton
      className={className}
      contentClassName={styles.contentPosition}
      renderButton={({ isOpen, toggle }) => (
        <a
          className={classNames(
            isOpen && styles.titleOpen, selectedItems.length > 0 && styles.titleHighlighted, styles.title,
          )}
          onClick={toggle}
        >
          {title}
          <ArrowDownIcon/>
        </a>
      )}
      renderContent={({ toggle }) => (
        <div className={styles.content}>
          {withSearch && (
            <div className={styles.searchContainer}>
              <Input className={styles.search} placeholder={placeholder} onChange={onSearchQueryChange}/>
              {themeValue({
                bel: <SearchIconBel width={16} height={16}/>,
                default: <SearchIcon width={16} height={16}/>
              })}
            </div>
          )}
          <div className={styles.list}>
            {!!selected.length && (
              <div className={styles.selectedList}>
                {_.map(selected.sort(), (value) => (
                  <div key={value._id} className={styles.listItem}>
                    <Checkbox value={value._id} onChange={_.partial(onUnselect, value)} checked>
                      {value.name}
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.optionsList}>
              {_.map(totalOptions, (value) => (
                <div key={value._id} className={styles.listItem}>
                  <Checkbox
                    value={value._id}
                    onChange={_.partial(onSelect, value)}
                    checked={false}
                    disabled={!!disabledArray.find((i) => i.name === value.name)}>
                    {value.name}
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <button
              className={styles.clearButton}
              onClick={() => {
                onReset();
                toggle();
              }}
            >
              очистить
            </button>
            <button
              className={styles.submitButton}
              onClick={() => {
                onSubmit();
                toggle();
              }}
            >
              применить
            </button>
          </div>
        </div>
      )}
    />
  );
}
